import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Box, Alert, Snackbar } from '@mui/material';

// Context Providers
import { ThemeProvider } from './contexts/ThemeContext.jsx';
import { LanguageProvider } from './contexts/LanguageContext.jsx';
import { AuthProvider } from './contexts/AuthContext.jsx';
import { BookingProvider } from './contexts/BookingContext.jsx';
import { GarageProvider } from './contexts/GarageContext.jsx';

// Components
import Navigation from './components/Navigation.jsx';
import Footer from './components/Footer.jsx';
import ProtectedRoute from './components/ProtectedRoute.jsx';
import RoleBasedRedirect from './components/RoleBasedRedirect.jsx';

// Pages
import HomePage from './pages/HomePage.jsx';
import LoginPage from './pages/LoginPage.jsx';
import GaragePage from './pages/GaragePage.jsx';
import ProductSelectionPage from './pages/ProductSelectionPage.jsx';
import AppointmentPage from './pages/AppointmentPage.jsx';
import SummaryPage from './pages/SummaryPage.jsx';
import ConfirmationPage from './pages/ConfirmationPage.jsx';
import BookingsPage from './pages/BookingsPage.jsx';
import HelpPage from './pages/HelpPage.jsx';
import PaymentSuccessPage from './pages/PaymentSuccessPage.jsx';
import PaymentCancelPage from './pages/PaymentCancelPage.jsx';
import GarageAdminDashboard from './pages/GarageAdminDashboard.jsx';
import SystemManagement from './pages/SystemManagement.jsx';
import DashboardPage from './pages/DashboardPage.jsx';

function App() {
  const [errorMessage, setErrorMessage] = useState('');
  const [showError, setShowError] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('info');
  const [showSnackbar, setShowSnackbar] = useState(false);

  useEffect(() => {
    // Check for stored inactive account error
    const storedError = localStorage.getItem('inactiveAccountError');
    if (storedError) {
      setErrorMessage(storedError);
      setShowError(true);
      localStorage.removeItem('inactiveAccountError'); // Clear it after showing
    }

    // Listen for login errors
    const handleLoginError = (event) => {
      setErrorMessage(event.detail.message);
      setShowError(true);
    };

    // Listen for account inactive events
    const handleAccountInactive = (event) => {
      setErrorMessage(event.detail.message);
      setShowError(true);
    };

    // Listen for snackbar events
    const handleShowSnackbar = (event) => {
      setSnackbarMessage(event.detail.message);
      setSnackbarSeverity(event.detail.severity || 'info');
      setShowSnackbar(true);
      
      // Custom auto-hide duration for success messages
      if (event.detail.autoHideDuration) {
        setTimeout(() => {
          setShowSnackbar(false);
        }, event.detail.autoHideDuration);
      }
    };

    window.addEventListener('loginError', handleLoginError);
    window.addEventListener('accountInactive', handleAccountInactive);
    window.addEventListener('showSnackbar', handleShowSnackbar);

    return () => {
      window.removeEventListener('loginError', handleLoginError);
      window.removeEventListener('accountInactive', handleAccountInactive);
      window.removeEventListener('showSnackbar', handleShowSnackbar);
    };
  }, []);

  const handleCloseError = () => {
    setShowError(false);
    setErrorMessage('');
  };

  const handleCloseSnackbar = () => {
    setShowSnackbar(false);
    setSnackbarMessage('');
  };

  return (
    <ThemeProvider>
        <LanguageProvider>
          <AuthProvider>
            <GarageProvider>
              <BookingProvider>
                <Router>
                <Box sx={{ 
                  minHeight: '100vh',
                  display: 'flex',
                  flexDirection: 'column',
                  backgroundColor: 'background.default',
                }}>
                  <Navigation />
                  
                  <Box 
                    component="main" 
                    sx={{ 
                      flexGrow: 1,
                      display: 'flex',
                      flexDirection: 'column',
                    }}
                  >
                    <RoleBasedRedirect>
                      <Routes>
                      {/* Public Routes */}
                      <Route path="/" element={<HomePage />} />
                      <Route path="/login" element={<LoginPage />} />
                      <Route path="/garages" element={<GaragePage />} />
                      <Route path="/products" element={<ProductSelectionPage />} />
                      <Route path="/appointment" element={<AppointmentPage />} />
                      <Route path="/help" element={<HelpPage />} />
                      
                      {/* Order Summary - No auth required, auth checked on payment button */}
                      <Route path="/summary" element={<SummaryPage />} />
                      
                      {/* Payment Routes - Handle Stripe redirects */}
                      <Route path="/payment/success" element={<PaymentSuccessPage />} />
                      <Route path="/payment/cancel" element={<PaymentCancelPage />} />
                      
                      {/* Protected Routes */}
                      <Route 
                        path="/confirmation/:bookingId" 
                        element={
                          <ProtectedRoute>
                            <ConfirmationPage />
                          </ProtectedRoute>
                        } 
                      />
                      <Route 
                        path="/confirmation" 
                        element={
                          <ProtectedRoute>
                            <ConfirmationPage />
                          </ProtectedRoute>
                        } 
                      />
                      <Route 
                        path="/bookings" 
                        element={
                          <ProtectedRoute>
                            <BookingsPage />
                          </ProtectedRoute>
                        } 
                      />
                      
                      {/* Role-based Dashboard Routes */}
                      <Route 
                        path="/dashboard" 
                        element={
                          <ProtectedRoute requiredRole="garage_admin">
                            <DashboardPage />
                          </ProtectedRoute>
                        } 
                      />
                      <Route 
                        path="/garage-admin" 
                        element={
                          <ProtectedRoute requiredRole="garage_admin">
                            <GarageAdminDashboard />
                          </ProtectedRoute>
                        } 
                      />
                      <Route 
                        path="/super-admin" 
                        element={
                          <ProtectedRoute requiredRole="super_user">
                            <SystemManagement />
                          </ProtectedRoute>
                        } 
                      />
                      
                      {/* Redirect unknown routes to home */}
                      <Route path="*" element={<Navigate to="/" replace />} />
                      </Routes>
                    </RoleBasedRedirect>
                  </Box>
                  
                  <Footer />
                </Box>
                
                {/* Global Error Message Display */}
                <Snackbar
                  open={showError}
                  onClose={handleCloseError}
                  anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                  sx={{ zIndex: 99999 }}
                >
                  <Alert 
                    onClose={handleCloseError} 
                    severity="error"
                    sx={{ 
                      backgroundColor: '#d32f2f',
                      color: '#ffffff'
                    }}
                  >
                    {errorMessage}
                  </Alert>
                </Snackbar>

                {/* Global Snackbar for Login Status */}
                <Snackbar
                  open={showSnackbar}
                  autoHideDuration={snackbarMessage === 'Logging in...' ? null : 4000}
                  onClose={handleCloseSnackbar}
                  anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                  sx={{ zIndex: 99999 }}
                >
                  <Alert 
                    onClose={handleCloseSnackbar} 
                    severity={snackbarSeverity}
                    variant="filled"
                  >
                    {snackbarMessage}
                  </Alert>
                </Snackbar>
                </Router>
              </BookingProvider>
            </GarageProvider>
          </AuthProvider>
        </LanguageProvider>
    </ThemeProvider>
  );
}

export default App;